/* eslint-disable @typescript-eslint/no-unused-vars */
import { Button, Combobox, createTheme, Divider, Input, MantineColorsTuple, MantineTheme, Menu, MultiSelect, NavLink, Popover, Radio, Select, Tabs, TagsInput, Text, Textarea } from '@mantine/core';
import cn from '../utils/styleHelpers';

// Custom colors
const mainColors: MantineColorsTuple = [
  '#f5f2fd',
  '#ebe5fb',
  '#ded3f8',
  '#cab8f4',
  '#ad91ee',
  '#8c65e7',
  '#7141e1',
  '#5822d8',
  '#4a1cb5',
  '#3d1796',
];

const textColors: MantineColorsTuple = [
  "#F3F4F6",
  "#E5E7EB",
  "#D1D5DB",
  "#9CA3AF",
  "#6B7280",
  "#4B5563",
  "#374151",
  "#1F2937",
  "#111827",
  "#111827", // Repeated last color to make it 10 elements
];

const theme = createTheme({
  fontFamily: 'Inter, sans-serif',
  fontFamilyMonospace: 'Monaco, Courier, monospace',
  headings: { fontFamily: 'Space Grotesk, sans-serif' },
  primaryShade: 5,
  colors: {
    main: mainColors,
    text: textColors,
    // Add Mantine's default emerald palette for the green buttons
    emerald: [
      '#ebfef6',
      '#ddf9ea',
      '#c0f2d2',
      '#a1e9b8',
      '#87e0a1',
      '#73d991',
      '#66d487',
      '#56b975',
      '#499f64',
      '#368252',
    ],
    sidebar: [
      '#FBFBFD', // 50
      '#F7F7FA', // 100 - bg
      '#EFEFF5', // 200 - hover
      '#E2E2EA', // 300
      '#C7C7D5', // 400
      '#A8A8C0', // 500
      '#757575', // 600 - text
      '#525252', // 700
      '#262626', // 800
      '#171717', // 900
    ],
    ai: [
      '#EEFBF6',
      '#D4F6E9',
      '#AEEEDB',
      '#7FE2CA',
      '#4BD3B6',
      '#23BF9F',
      '#059669', // ready/glow
      '#047857',
      '#065F46',
      '#064E3B',
    ],
  },
  fontSizes: {
    xs: '0.625rem', // 10px
    sm: '0.75rem', // 12px
    md: '0.875rem', // 14px
  },
  primaryColor: 'main',
  defaultRadius: 'md',

  // Advanced button theming with Tailwind and Mantine
  components: {
    Radio: Radio.extend({
      classNames: {
        icon: "text-purple-400"
      }
    }),
    Textarea: Textarea.extend({
      classNames: (_theme: MantineTheme, props: any) => ({
        input: "p-2 pt-1",
        root: cn(
          // Base styles for all buttons
          'leading-6',
          {
            'leading-1/2': props.size === 'xs' || props.size === 'sm',
          }
        ),
      }),
    }),
    Text: Text.extend({
      classNames: (_theme: MantineTheme, props: any) => ({
        root: cn(
          // Base styles for all buttons
          'leading-6',
          {
            'leading-1/2': props.size === 'xs' || props.size === 'sm',
          }
        ),
      }),
    }),
    NavLink: NavLink.extend({
      classNames: (_theme: MantineTheme, props: any) => ({
        root: cn(
          // Base styles for all buttons
          'focus:outline-none transition-colors py-1.5 font-semibold rounded-md text-gray-800',
          {
            // Sidebar variant
            'w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200': props.variant === 'sidebar',
            // Active state
            'bg-sidebar-active text-sidebar-text-active': props.variant === 'sidebar' && props.active,
            // Inactive state
            'text-sidebar-text hover:bg-sidebar-hover hover:text-foreground': props.variant === 'sidebar' && !props.active,
          }
        ),
        label: cn({
          'text-sm font-medium': props.variant === 'sidebar',
        }),
        section: cn({
          'mr-3': props.variant === 'sidebar' && props.leftSection, // Adjust icon spacing
        })
      }),
    }),
    Tabs: Tabs.extend({
      classNames: (_theme: MantineTheme, props: any) => ({
        list: cn(
          {
            // Base styles for all buttons
            '!w-fit rounded-md bg-slate-50 p-1': props.variant === "pills",
          }
        ),
      }),
    }),
    Button: Button.extend({
      classNames: (_theme: MantineTheme, props: any) => ({
        root: cn(
          "focus:outline-none transition-colors rounded-md border font-medium disabled:opacity-60 disabled:cursor-not-allowed",
          {
            "h-7 px-2 text-xs": props.size === "xs",
            "h-8 px-3 text-sm": props.size === "sm",
            "h-9 px-4 text-sm": !props.size || props.size === "md",
            "h-10 px-5 text-base": props.size === "lg",
            "h-11 px-6 text-lg": props.size === "xl",
          },
          {
            // Primary button (matches AccountsHeader default CTA behavior)
            "bg-primary-600 text-white border-primary-600 hover:bg-primary-700 hover:border-primary-700":
              props.variant === "default"
              || props.variant === "filled"
              || props.variant === "purple"
              || (!props.variant && !props.color),

            // Standard outline button (matches AccountsHeader toolbar buttons)
            "bg-white text-gray-700 border-gray-300 hover:bg-gray-50 hover:text-gray-900":
              props.variant === "outline" || props.variant === "white",

            // Ghost button (matches AccountsHeader icon-only settings button)
            "bg-transparent text-gray-500 border-transparent hover:bg-gray-100 hover:text-gray-700":
              props.variant === "ghost" || props.variant === "subtle",

            // Keep existing custom variants used in other surfaces
            "bg-emerald-600 text-white border-emerald-600 hover:bg-emerald-700 hover:border-emerald-700":
              props.variant === "green",
            "border-primary-500 bg-primary-50 text-primary-700 hover:bg-primary-100 hover:text-primary-800":
              props.variant === "light",
            "border-purple-500 bg-transparent text-purple-500 hover:text-purple-600 hover:border-purple-600 hover:bg-gray-50":
              props.variant === "opaque",
          },
        ),
      }),
    }),
    MultiSelect: MultiSelect.extend({
      classNames: (_theme: MantineTheme, props: any) => ({
        dropdown: cn(
          'bg-white border border-gray-200 shadow-lg',
        ),
        option: cn(

          // Base styles for all buttons
          'focus:outline-none transition-colors  text-xs p-3 py-2 hover:bg-gray-200 [data-combobox-selected="true"]:bg-primary-200',
        ),
        pillsList: cn(
          'flex flex-wrap gap-1',
        ),
        pill: cn(
          'border border-primary-200 text-xs bg-white text-primary-600',
        ),
        input: cn('flex items-center')

      }),
    }),
    Divider: Divider.extend({
      defaultProps: {
        color: 'rgba(61, 60, 60, 0.1)',
        variant: 'solid',
      },
      classNames: {
        label: "text-black",
      }
    }),
    TagsInput: TagsInput.extend({
      classNames: (_theme: MantineTheme, props: any) => ({
        input: cn(
          'py-2',
        ),
        pill: cn(
          'border border-primary-200 text-xs bg-white text-primary-600',
        ),
      })
    }),
    Select: Select.extend({
      classNames: (_theme: MantineTheme, props: any) => ({
        dropdown: cn(
          'bg-white border border-gray-200 shadow-lg',
        ),
        option: cn(

          // Base styles for all buttons
          'focus:outline-none transition-colors  text-xs p-3 py-2 hover:bg-gray-200 [data-combobox-selected="true"]:bg-primary-200',
        ),
        input: cn(
          // Base styles for all buttons
          'focus:outline-none border border-gray-300 bg-white text-gray-700 transition-colors hover:bg-white hover:text-gray-700 focus:bg-white focus:text-gray-800 focus:border-gray-400 py-0 rounded-md',
          {
            'px-2 text-sm': props.size === 'sm',
            'px-2 text-xs h-4': props.size === 'xs',
          }
        ),
      }),
    }),
    Input: Input.extend({
      classNames: (_theme: MantineTheme, props: any) => ({
        input: cn(
          // Base styles for all buttons
          'focus:outline-none border border-gray-300 bg-white text-gray-700 transition-colors hover:bg-white hover:text-gray-700 focus:bg-white focus:text-gray-800 focus:border-gray-400 py-0 rounded-md',
        ),
      })
    }),
    Combobox: Combobox.extend({
      classNames: (_theme: MantineTheme, props: any) => ({
        dropdown: cn(
          'bg-white border border-gray-200 shadow-lg',
        ),
        option: cn(

          // Base styles for all buttons
          'focus:outline-none transition-colors  text-xs p-3 py-2 hover:bg-gray-200 [data-combobox-selected="true"]:bg-primary-200',
        ),
        input: cn(
          // Base styles for all buttons
          'focus:outline-none border border-gray-300 bg-white text-gray-700 transition-colors hover:bg-white hover:text-gray-700 focus:bg-white focus:text-gray-800 focus:border-gray-400 py-0 rounded-md',
          {
            'px-2 text-sm': props.size === 'sm',
            'px-2 text-xs h-4': props.size === 'xs',
          }
        ),
      }),
    }),
    Popover: Popover.extend({
      classNames: (_theme: MantineTheme, props: any) => ({
        dropdown: cn(
          'bg-white border border-gray-200 shadow-lg',
        ),
      }),
    }),
    Menu: Menu.extend({
      classNames: (_theme: MantineTheme, props: any) => ({
        dropdown: cn(
          'bg-white border border-gray-200 shadow-lg',
        ),
        item: cn(

          // Base styles for all buttons
          'focus:outline-none transition-colors  text-xs p-3 py-2 hover:bg-gray-200',
        ),
      }),
    }),
  },

});

export default theme;
